import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, message, Typography, Empty, Tooltip } from 'antd';
import dayjs from 'dayjs';
import ApiService from '../../APIServices/ApiService';

const { Text, Paragraph } = Typography;

/**
 * What an affiliate has earned, across BOTH sales channels.
 *
 * It used to read a ledger that covered only Book sales through their own
 * catalogue page, so a partner who also referred storefront sales was shown a
 * number that was true of neither. It now reads the one commission ledger —
 * the same rows the admin console shows — so the two can never disagree.
 *
 * MONEY ARRIVES AS INTEGER MINOR UNITS in EUR and is only divided for display.
 * The page never does commission arithmetic, so it cannot drift from the
 * ledger.
 */

const money = (minor, currency = 'EUR') =>
    ((minor || 0) / 100).toLocaleString(undefined, { style: 'currency', currency });

const monthLabel = (y, m) => dayjs(`${y}-${String(m).padStart(2, '0')}-01`).format('MMM YYYY');

const rate = (bp) => `${Number.isInteger(bp / 100) ? bp / 100 : (bp / 100).toFixed(2)}%`;

const CATEGORY_LABEL = { travel: 'Travel guides', language: 'Language guides', esim: 'eSIMs' };

const CHANNEL_LABEL = {
    youtra: { label: 'Storefront', color: 'blue' },
    affiliate_guide: { label: 'Your own page', color: 'purple' },
};

const STATUS = {
    pending: { color: 'gold', label: 'Settling' },
    approved: { color: 'blue', label: 'Payable' },
    paid: { color: 'green', label: 'Paid' },
    reversed: { color: 'red', label: 'Reversed' },
};

const MyEarnings = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                setData(await ApiService.getMyEarnings());
            } catch (e) {
                setError(e?.response?.data?.message || 'Could not load your earnings.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <Spin style={{ display: 'block', margin: '60px auto' }} size="large" />;
    if (error) return <Card><Empty description={error} /></Card>;
    if (!data) return null;

    const { totals, monthly, byChannel, byCategory, recent, currency = 'EUR' } = data;

    return (
        <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic title="Earned in total" value={money(totals.lifetimeMinor, currency)} />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        {/* Settling, not "pending payout": it is inside the
                            30-day window and will become payable on its own. */}
                        <Tooltip title="Earned, and inside the 30-day settlement window.">
                            <Statistic
                                title="Settling"
                                value={money(totals.pendingMinor, currency)}
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Tooltip>
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Tooltip title="Settled and payable at the next payout run.">
                            <Statistic
                                title="Payable now"
                                value={money(totals.payableMinor, currency)}
                                valueStyle={{ color: '#1677ff' }}
                            />
                        </Tooltip>
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Paid out"
                            value={money(totals.paidMinor, currency)}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card title="Month by month" size="small">
                        <Table
                            rowKey={(r) => `${r.year}-${r.month}`}
                            dataSource={monthly}
                            pagination={false}
                            size="small"
                            columns={[
                                { title: 'Month', key: 'm', render: (_, r) => monthLabel(r.year, r.month) },
                                { title: 'Sales', dataIndex: 'sales', key: 's', align: 'right' },
                                {
                                    title: 'Earned',
                                    dataIndex: 'earnedMinor',
                                    key: 'e',
                                    align: 'right',
                                    render: (v) => money(v, currency),
                                },
                            ]}
                            locale={{ emptyText: 'No sales yet.' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card title="Where it came from" size="small">
                        <Table
                            rowKey={(r) => `${r.channel}-${r.level}`}
                            dataSource={byChannel}
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: 'Source',
                                    key: 'c',
                                    render: (_, r) => (
                                        <>
                                            <Tag color={(CHANNEL_LABEL[r.channel] || {}).color}>
                                                {(CHANNEL_LABEL[r.channel] || {}).label || r.channel}
                                            </Tag>
                                            {/* Level above 0 means a sub-affiliate
                                                made the sale and this is the share
                                                that came up the chain. */}
                                            {r.level > 0 && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    via a sub-affiliate
                                                </Text>
                                            )}
                                        </>
                                    ),
                                },
                                { title: 'Sales', dataIndex: 'sales', key: 's', align: 'right' },
                                {
                                    title: 'Earned',
                                    dataIndex: 'earnedMinor',
                                    key: 'e',
                                    align: 'right',
                                    render: (v) => money(v, currency),
                                },
                            ]}
                            locale={{ emptyText: 'No sales yet.' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Card title="By product" size="small" style={{ marginTop: 16 }}>
                <Table
                    rowKey="category"
                    dataSource={byCategory}
                    pagination={false}
                    size="small"
                    columns={[
                        {
                            title: 'Product',
                            dataIndex: 'category',
                            key: 'c',
                            render: (c) => CATEGORY_LABEL[c] || c,
                        },
                        { title: 'Sold', dataIndex: 'sales', key: 's', align: 'right' },
                        {
                            title: 'Earned',
                            dataIndex: 'earnedMinor',
                            key: 'e',
                            align: 'right',
                            render: (v) => money(v, currency),
                        },
                    ]}
                    locale={{ emptyText: 'No sales yet.' }}
                />
            </Card>

            <Card title="Recent sales" size="small" style={{ marginTop: 16 }}>
                <Table
                    rowKey="id"
                    dataSource={recent}
                    pagination={false}
                    size="small"
                    scroll={{ x: 760 }}
                    columns={[
                        {
                            title: 'Date',
                            dataIndex: 'purchasedAt',
                            key: 'd',
                            width: 110,
                            render: (v) => dayjs(v).format('DD MMM YYYY'),
                        },
                        {
                            title: 'Product',
                            dataIndex: 'productName',
                            key: 'p',
                            render: (n, r) => (
                                <>
                                    <div>{n || '—'}</div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {CATEGORY_LABEL[r.category] || r.category}
                                    </Text>
                                </>
                            ),
                        },
                        {
                            title: 'Customer paid',
                            dataIndex: 'baseMinor',
                            key: 'b',
                            align: 'right',
                            width: 160,
                            // With the list price and the discount beneath it.
                            // "EUR 0.00 earned" on its own reads as broken
                            // tracking; "EUR 8.60 list, EUR 8.60 off" explains
                            // itself in one line.
                            render: (v, r) => (
                                <>
                                    <div>{money(v, currency)}</div>
                                    {r.discountMinor > 0 && (
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            {money(r.listMinor, currency)} · {money(r.discountMinor, currency)} off
                                        </Text>
                                    )}
                                </>
                            ),
                        },
                        { title: 'Rate', dataIndex: 'rateBp', key: 'r', align: 'right', width: 70, render: rate },
                        {
                            title: 'You earned',
                            dataIndex: 'amountMinor',
                            key: 'e',
                            align: 'right',
                            width: 120,
                            render: (v) => <strong>{money(v, currency)}</strong>,
                        },
                        {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 's',
                            width: 120,
                            render: (s, r) => (
                                <Tooltip
                                    title={s === 'pending' ? `Payable ${dayjs(r.payableAt).format('DD MMM YYYY')}` : undefined}
                                >
                                    <Tag color={(STATUS[s] || {}).color}>{(STATUS[s] || {}).label || s}</Tag>
                                </Tooltip>
                            ),
                        },
                    ]}
                    locale={{ emptyText: 'Nothing yet. Sales appear here once a customer pays.' }}
                />
                <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                    Commission settles 30 days after the sale, then becomes payable. Payouts are made once your
                    payable balance reaches {money(5000, currency)}.
                </Paragraph>
            </Card>
        </div>
    );
};

export default MyEarnings;
